import type { TruckServiceOverrideInput } from '@chukua/common';
import { Alert, Form, Modal, Switch } from 'antd';
import { useEffect } from 'react';
import { IntervalFields } from '../../../components/IntervalFields';
import type { TruckServiceItemView } from '../truck-service.api';

interface OverrideModalProps {
  /** The plan row being changed for this truck. */
  item?: TruckServiceItemView;
  error: string | null;
  submitting: boolean;
  onSubmit: (item: { id: string; name: string }, input: TruckServiceOverrideInput) => Promise<boolean>;
  onClose: () => void;
}

type FormValues = Pick<TruckServiceOverrideInput, 'isExcluded' | 'intervalKm' | 'intervalDays' | 'intervalHours'>;

/** This truck's own interval for an item, or leaving the item out for it. Either replaces the class's value for this truck only. */
export function OverrideModal({ item, error, submitting, onSubmit, onClose }: OverrideModalProps) {
  const [form] = Form.useForm<FormValues>();
  const isExcluded = Form.useWatch('isExcluded', form);

  useEffect(() => {
    if (item) form.setFieldsValue({ isExcluded: false, intervalKm: item.intervalKm, intervalDays: item.intervalDays, intervalHours: item.intervalHours });
  }, [item, form]);

  async function submit(values: FormValues) {
    if (!item) return;
    const excluded = Boolean(values.isExcluded);
    const input = excluded ? { isExcluded: true, intervalKm: null, intervalDays: null, intervalHours: null } : { ...values, isExcluded: false };
    if (!excluded && input.intervalKm == null && input.intervalDays == null && input.intervalHours == null) {
      form.setFields([{ name: 'intervalKm', errors: ['Give at least one interval: km, days or hours'] }]);
      return;
    }
    if (await onSubmit({ id: item.serviceItemId, name: item.name }, input as TruckServiceOverrideInput)) onClose();
  }

  return (
    <Modal
      open={item !== undefined}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText="Save for this truck"
      okButtonProps={{ loading: submitting }}
      title={item ? `${item.name} for this truck` : ''}
      destroyOnHidden
    >
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<FormValues> form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={submitting}>
        <Form.Item name="isExcluded" label="Leave this item out for this truck" valuePropName="checked">
          <Switch />
        </Form.Item>
        {!isExcluded && <IntervalFields hint="Replaces the class's interval for this truck only." />}
      </Form>
    </Modal>
  );
}
